"use client";

import { useEffect, useMemo, useState } from "react";

import { StepNavigation } from "@/components/backoffice/step-navigation";
import { VehicleAssignmentStepProgress } from "@/components/backoffice/cat/vehicle-assignment/vehicle-assignment-step-progress";
import { VehicleAssignmentAvsSteps } from "@/components/backoffice/cat/vehicle-assignment/avs/vehicle-assignment-avs-steps";
import { useStep } from "@/hooks/use-step-handler";
import { getAvsVehicleAssignmentVisibleDefinitions } from "@/lib/cat/vehicle-assignment-avs-step-definitions";
import {
  toVehicleAssignmentProgressSteps,
  type VehicleAssignmentFlowContext,
} from "@/lib/cat/vehicle-assignment-step-definitions";
import type { Kvps } from "@/lib/kvps";

function VehicleAssignmentAvsFlow({
  currentKvps,
}: {
  currentKvps?: Kvps | null;
}) {
  const { handleStepChange, step } = useStep();
  const [canProceed, setCanProceed] = useState(true);

  const ctx: VehicleAssignmentFlowContext = useMemo(
    () => ({ kvps: currentKvps ?? null }),
    [currentKvps],
  );

  const visibleDefinitions = useMemo(
    () => getAvsVehicleAssignmentVisibleDefinitions(ctx),
    [ctx],
  );

  const stepsAvs = useMemo(
    () => toVehicleAssignmentProgressSteps(visibleDefinitions),
    [visibleDefinitions],
  );

  const maxStep = stepsAvs.length;
  const minStep = 1;

  useEffect(() => {
    if (step > maxStep) {
      handleStepChange(maxStep);
    }
  }, [handleStepChange, maxStep, step]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-8">
        <div className="col-span-1">
          <VehicleAssignmentStepProgress currentStep={step} steps={stepsAvs} />
        </div>

        <div className="col-span-3">
          <VehicleAssignmentAvsSteps
            currentKvps={currentKvps}
            onCanProceedChange={setCanProceed}
          />
          <StepNavigation
            isNextButtonDisabled={!canProceed}
            onNext={() => handleStepChange(Math.min(step + 1, maxStep))}
            onPrevious={() => handleStepChange(Math.max(step - 1, minStep))}
            showNextButton={step < maxStep}
            showPreviousButton={step > minStep}
          />
        </div>
      </div>
    </div>
  );
}

export { VehicleAssignmentAvsFlow };
